import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Building2, MapPin, Users, ArrowLeft, ExternalLink, Filter,
  Briefcase, CheckCircle, Star, Clock, DollarSign, Search, X
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useAuth } from '@/context/AuthContext';
import { getAuthToken } from '@/utils/secureStorage';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export default function EmployerDirectory() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [employers, setEmployers] = useState([]);
  const [sectors, setSectors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [sectorFilter, setSectorFilter] = useState('all');
  const [userStats, setUserStats] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const [employersRes, sectorsRes] = await Promise.all([
        fetch(`${API}/api/workforce/employers`),
        fetch(`${API}/api/workforce/sectors`)
      ]);

      if (employersRes.ok) {
        const data = await employersRes.json();
        setEmployers(data.employers);
      }
      if (sectorsRes.ok) {
        const data = await sectorsRes.json();
        setSectors(data.sectors);
      }

      // Get user stats if logged in
      if (user) {
        const token = getAuthToken();
        const statsRes = await fetch(`${API}/api/workforce/stats`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (statsRes.ok) {
          setUserStats(await statsRes.json());
        }
      }
    } catch (err) {
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleReferralRequest = async (employer) => {
    if (!user) {
      toast.error('Please log in to request a referral');
      return;
    }

    if (!userStats?.enrolled) {
      toast.error('Please enroll in the program first');
      navigate('/workforce/assessment');
      return;
    }

    try {
      const token = getAuthToken();
      const res = await fetch(`${API}/api/workforce/participant/referral`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          employer_id: employer.id,
          notes: `Referral requested for ${employer.name}`
        })
      });

      if (res.ok) {
        const data = await res.json();
        toast.success('Referral requested! Check your pathway for next steps.');
      } else {
        throw new Error('Failed to create referral');
      }
    } catch (err) {
      toast.error('Failed to request referral');
    }
  };

  const filteredEmployers = employers.filter(e => {
    const matchesSearch = searchQuery === '' || 
      e.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      e.location.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesSector = sectorFilter === 'all' || 
      e.sector === sectorFilter || 
      (Array.isArray(e.sector) && e.sector.includes(sectorFilter));
    
    return matchesSearch && matchesSector;
  });

  const getSectorName = (sectorId) => {
    if (Array.isArray(sectorId)) {
      return sectorId.map(id => sectors.find(s => s.id === id)?.name || id).join(', ');
    }
    return sectors.find(s => s.id === sectorId)?.name || sectorId;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-purple-50/30" data-testid="employer-directory">
      {/* Header */}
      <div className="bg-gradient-to-br from-purple-600 to-violet-700 text-white py-12">
        <div className="container mx-auto px-4">
          <Link to="/workforce" className="inline-flex items-center text-white/80 hover:text-white mb-6">
            <ArrowLeft className="h-4 w-4 mr-2" /> Back to Workforce Hub
          </Link>
          
          <div className="flex items-start gap-4">
            <div className="p-4 bg-white/20 rounded-xl">
              <Building2 className="h-10 w-10" />
            </div>
            <div>
              <Badge className="mb-2 bg-white/20 text-white border-0">Employer Network</Badge>
              <h1 className="text-3xl md:text-4xl font-bold mb-2">Vetted Employer Partners</h1>
              <p className="text-purple-100 max-w-2xl">
                Companies committed to equitable hiring with "first look" opportunities for our participants.
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8 max-w-6xl">
        {/* Filters */}
        <Card className="mb-8">
          <CardContent className="py-4">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search employers..."
                  className="pl-10"
                />
              </div>
              <Select value={sectorFilter} onValueChange={setSectorFilter}>
                <SelectTrigger className="w-full md:w-[200px]">
                  <SelectValue placeholder="All Sectors" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Sectors</SelectItem>
                  {sectors.map(s => (
                    <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {(searchQuery || sectorFilter !== 'all') && (
                <Button variant="ghost" onClick={() => { setSearchQuery(''); setSectorFilter('all'); }}>
                  <X className="h-4 w-4 mr-1" /> Clear
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Results */}
        <p className="text-slate-600 mb-6">{filteredEmployers.length} employer partners</p>

        <div className="grid md:grid-cols-2 gap-6">
          {filteredEmployers.map((employer, idx) => (
            <motion.div
              key={employer.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: idx * 0.05 }}
            >
              <Card className="h-full hover:shadow-lg transition-shadow">
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div>
                      <CardTitle className="text-lg">{employer.name}</CardTitle>
                      <div className="flex items-center gap-2 text-sm text-slate-500 mt-1">
                        <MapPin className="h-4 w-4" />
                        {employer.location}
                      </div>
                    </div>
                    {employer.equity_commitment && (
                      <Badge className="bg-green-100 text-green-700">
                        <CheckCircle className="h-3 w-3 mr-1" /> Equity Committed
                      </Badge>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div>
                      <p className="text-sm text-slate-500">Sector</p>
                      <p className="font-medium text-slate-700">{getSectorName(employer.sector)}</p>
                    </div>
                    
                    <div>
                      <p className="text-sm text-slate-500">Open Positions</p>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {employer.positions_available?.slice(0, 3).map(pos => (
                          <Badge key={pos} variant="secondary" className="text-xs">{pos}</Badge>
                        ))}
                        {employer.positions_available?.length > 3 && (
                          <Badge variant="secondary" className="text-xs">+{employer.positions_available.length - 3} more</Badge>
                        )}
                      </div>
                    </div>

                    {employer.benefits && (
                      <div>
                        <p className="text-sm text-slate-500">Benefits</p>
                        <p className="text-sm text-slate-700">{employer.benefits.slice(0, 2).join(' • ')}</p>
                      </div>
                    )}

                    <div className="flex items-center justify-between pt-4 border-t">
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="text-xs">
                          Min Stage: {employer.min_candidate_stage.replace('_', ' ')}
                        </Badge>
                        {employer.hiring_commitment === 'second_chance' && (
                          <Badge className="bg-amber-100 text-amber-700 text-xs">Second Chance</Badge>
                        )}
                      </div>
                      <Button 
                        size="sm" 
                        className="bg-purple-600 hover:bg-purple-700"
                        onClick={() => handleReferralRequest(employer)}
                      >
                        Request Referral
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>

        {filteredEmployers.length === 0 && (
          <Card className="text-center py-12">
            <CardContent>
              <Building2 className="h-12 w-12 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-700 mb-2">No employers found</h3>
              <p className="text-slate-500">Try adjusting your filters.</p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
